import {
  Button,
  SidePanel,
  SidePanelActions,
  Typography,
  RadioGroup,
  RadioButton,
  Autocomplete,
  Option
} from '@getgo/chameleon-web-react-wrapper/register';
import { ColumnFilter, ColumnFiltersState } from '@tanstack/react-table';
import { ReactElement, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WindowsUpdatesFilterPanel.module.scss';
import { AutocompleteComponent } from '@getgo/chameleon-web';
import Tenant from '../../../core/models/Tenant';

interface WindowsUpdatesFilterPanelProps {
  isOpen: boolean;
  filtersState: ColumnFiltersState;
  onClose?: () => void;
  setColumnFilters?: (filters: ColumnFiltersState) => void;
  showSecurityUpdatesOnly: boolean;
  handleShowSecurityUpdatesFilterChange?: (securityUpdates: boolean) => void;
  handleTenantFilterChange?: (selectedTenants: Tenant[]) => void;
  tenantsAutocompleteRef: RefObject<AutocompleteComponent>;
  globalTenants: Tenant[];
}

export default function WindowsUpdatesFilterPanel({
  isOpen,
  filtersState,
  onClose,
  setColumnFilters,
  showSecurityUpdatesOnly,
  handleShowSecurityUpdatesFilterChange,
  handleTenantFilterChange,
  tenantsAutocompleteRef,
  globalTenants
}: WindowsUpdatesFilterPanelProps): ReactElement {
  const getValue = <T,>(id: string): T | null => (filtersState.find(f => f.id === id)?.value as T) ?? null;

  const { t } = useTranslation();
  const [securityUpdates, setSecurityUpdates] = useState(getValue<string>('securityUpdates') ?? '');
  const [tenantFilters, setTenantFilters] = useState([...(getValue<string>('tenants') ?? [])]);

  useEffect(() => {
    setSecurityUpdates(getValue<string>('securityUpdates') ?? '');
    setTenantFilters([...(getValue<string>('tenants') ?? [])]);
  }, [filtersState, getValue]);

  function mapToFilterState(): ColumnFiltersState {
    const deduplicate = (filter: ColumnFilter): ColumnFilter => ({
      id: filter.id,
      value: [...new Set(filter.value as string[])]
    });

    return [
      securityUpdates ? { id: 'securityUpdates', value: securityUpdates } : null,
      tenantFilters.length > 0 ? { id: 'tenants', value: tenantFilters } : null
    ].flatMap(f => {
      if (!f) return [];
      return Array.isArray(f.value) ? deduplicate(f) : f;
    });
  }

  return (
    <SidePanel
      isOpen={isOpen}
      closeLabel="Close"
      onClose={() => {
        onClose?.();
      }}
      headingStart={<Typography variant="heading-small">{t('windowsUpdatesFilter.title')}</Typography>}
      footer={
        <SidePanelActions>
          <Button
            variant="neutral"
            onClick={() => {
              tenantsAutocompleteRef.current?.clear();
              setSecurityUpdates('showAllUpdates');
              handleShowSecurityUpdatesFilterChange?.(false);
              handleTenantFilterChange?.([]);
              setColumnFilters?.([]);
            }}
            aria-label="clearFilter">
            {t('filterPanel.clearFilter')}
          </Button>
          <Button
            variant="primary"
            aria-label="applyFilter"
            onClick={() => {
              const selectedTenants =
                tenantsAutocompleteRef.current?.selectedOptions.map(option => {
                  return globalTenants.find(tenant => option.dataset.optionId === tenant.uuid);
                }) ?? [];
              handleTenantFilterChange?.(selectedTenants.filter(tenant => tenant !== undefined) as Tenant[]);
              handleShowSecurityUpdatesFilterChange?.(securityUpdates === 'showSecurityUpdates');
              setColumnFilters?.(mapToFilterState());
            }}>
            {t('filterPanel.applyFilter')}
          </Button>
        </SidePanelActions>
      }>
      <div className={styles.sidepanelContents}>
        <RadioGroup
          className={styles.filterCaption}
          value={showSecurityUpdatesOnly ? 'showSecurityUpdates' : 'showAllUpdates'}
          label={t('windowsUpdatesFilter.headers.securityUpdates')}
          aria-label="securityUpdates">
          <RadioButton
            value={'showAllUpdates'}
            onClick={() => setSecurityUpdates('showAllUpdates')}
            aria-label="showAllUpdates">
            <Typography variant="body-small">{t('windowsUpdatesSelector.showAllUpdates')}</Typography>
          </RadioButton>
          <RadioButton
            value={'showSecurityUpdates'}
            onClick={() => setSecurityUpdates('showSecurityUpdates')}
            aria-label="showSecurityUpdates">
            <Typography variant="body-small">{t('windowsUpdatesSelector.showSecurityUpdates')}</Typography>
          </RadioButton>
        </RadioGroup>
        {globalTenants.length >= 2 && (
          <Autocomplete
            ref={tenantsAutocompleteRef}
            className={styles.filterCaption}
            multiple={true}
            label={t('windowsUpdatesFilter.headers.tenants')}
            aria-label="tenants"
            clearButtonLabel="clear"
            toggleButtonLabel="toggle"
            placeholder="Select tenants"
            onChange={event => {
              setTenantFilters(
                event.detail.selectedOptions.map(
                  option => globalTenants.find(tenant => tenant.uuid === option.dataset.optionId)?.name ?? ''
                )
              );
            }}>
            {globalTenants.map(tenant => {
              return (
                <Option
                  key={tenant.uuid}
                  label={tenant.name}
                  id={tenant.uuid}
                  data-option-id={tenant.uuid}
                  value={tenant.name}
                  aria-label={tenant.name}>
                  <Typography>{tenant.name}</Typography>
                </Option>
              );
            })}
          </Autocomplete>
        )}
      </div>
    </SidePanel>
  );
}
